import React, { useEffect, useRef, useState } from 'react';
import {
    TAGLEVELS, DIVISION_VALUES, UNIT_VALUES, isDivisionLevel, isUnitLevel,
} from '../utils/Constants';
import { isEmpty } from '../utils/BasicUtilities';
import useLocalStorageLevel from '../contexts/levelHook';
import './components.css';

// Dropdown item pill (shared look with the navbar level picker).
const pillItemStyle = {
    backgroundColor: '#ede9fe',
    borderRadius: '9999px',
    padding: '0.3rem 0.8rem',
    fontWeight: 'bold',
    color: '#4c1d95',
    marginBottom: '0.25rem',
    border: 'none',
    textAlign: 'left',
    width: '100%',
    whiteSpace: 'nowrap',
    transition: 'all 0.3s',
    cursor: 'pointer',
};

// Self-contained level selector with the SAME master–detail behaviour as the navbar's
// "Change Level": hover SSC/HSC → Division panel, hover VARSITY-* → Unit panel, and it
// persists level + division/unit via the shared level hook. Reusable on the homepage.
//   variant="button" (default): navbar-style pill labelled "Change Level …".
//   variant="plain":            plain label-styled text of the current selection (homepage).
export default function LevelPicker({ variant = 'button', triggerClassName, triggerStyle }) {
    const [currentLevel, setCurrentLevel, levelExtra] = useLocalStorageLevel();
    const { division: currentDivision, unit: currentUnit, setSelection } = levelExtra;

    const [open, setOpen] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [flyoutFor, setFlyoutFor] = useState(null); // { kind, level }
    const rootRef = useRef(null);

    useEffect(() => {
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
                setFlyoutFor(null);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, []);

    const close = () => { setOpen(false); setFlyoutFor(null); };
    const pickLevel = (lvl) => { setCurrentLevel(lvl); close(); };
    const pickDivision = (lvl, division) => { setSelection(lvl, { division }); close(); };
    const pickUnit = (lvl, unit) => { setSelection(lvl, { unit }); close(); };

    const activeStyle = (lvl) => (currentLevel === lvl
        ? { backgroundColor: '#c4b5fd', color: '#4c1d95', fontWeight: 'bold' } : {});

    const plain = variant === 'plain';
    const dimText = currentDivision ? `Division: ${currentDivision}` : (currentUnit ? `Unit: ${currentUnit}` : '');
    const label = plain
        ? (isEmpty(currentLevel)
            ? 'Select level ▾'
            : `${currentLevel}${dimText ? '  ·  ' + dimText : ''} ▾`)
        : (isEmpty(currentLevel)
            ? 'Set Level ▾'
            : `Change Level ${currentLevel}${dimText ? ' · ' + dimText : ''} ▾`);
    const triggerCls = triggerClassName != null ? triggerClassName : (plain ? '' : 'nav-pill');
    // Plain trigger: label-styled text (no button chrome) matching the "Level:" caption.
    const plainStyle = {
        background: 'transparent', border: 'none', padding: 0,
        color: '#4c1d95', fontWeight: 700, letterSpacing: '0.02em', cursor: 'pointer',
    };
    const resolvedTriggerStyle = plain ? { ...plainStyle, ...(triggerStyle || {}) } : triggerStyle;

    return (
        <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                className={triggerCls}
                style={resolvedTriggerStyle}
                onClick={() => setOpen(o => !o)}
            >
                {label}
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute', top: '2.9rem', left: 0,
                        display: 'flex', alignItems: 'flex-start', gap: '0.4rem', zIndex: 9999
                    }}
                    onMouseLeave={() => setFlyoutFor(null)}
                    onClick={close}
                >
                    <div style={{ position: 'relative' }}>
                        <ul className="kp-scroll-menu" style={{
                            margin: 0, backgroundColor: '#ffffff', border: '1px solid #ddd6fe',
                            borderRadius: '0.65rem', padding: '0.5rem', minWidth: 'min(230px, 88vw)',
                            maxWidth: '88vw', maxHeight: '50vh', overflowY: 'scroll', overflowX: 'hidden',
                            listStyle: 'none', boxShadow: '0 18px 44px rgba(76,29,149,0.28)'
                        }}>
                            {TAGLEVELS.map(item => {
                                if (item.submenu) {
                                    return (
                                        <React.Fragment key={item.value}>
                                            <li style={{ padding: '0.35rem 0.5rem 0.15rem', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.4px', color: '#9ca3af', textTransform: 'uppercase' }}>
                                                {item.label}
                                            </li>
                                            {item.submenu.map(sub => {
                                                const withUnit = isUnitLevel(sub.value);
                                                const openHere = withUnit && flyoutFor && flyoutFor.kind === 'Unit' && flyoutFor.level === sub.value;
                                                return (
                                                    <li key={sub.value}>
                                                        <button
                                                            style={{
                                                                ...pillItemStyle,
                                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                                ...((hoveredItem === `sub-${sub.value}` || openHere) ? { backgroundColor: '#ddd6fe', color: '#5b21b6' } : {}),
                                                                ...activeStyle(sub.value)
                                                            }}
                                                            onClick={() => pickLevel(sub.value)}
                                                            onMouseEnter={() => { setHoveredItem(`sub-${sub.value}`); setFlyoutFor(withUnit ? { kind: 'Unit', level: sub.value } : null); }}
                                                            onMouseLeave={() => setHoveredItem(null)}
                                                        >
                                                            <span>{sub.label}</span>{withUnit ? <span aria-hidden>▸</span> : null}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                }
                                const isDiv = isDivisionLevel(item.value);
                                const openHere = isDiv && flyoutFor && flyoutFor.kind === 'Division' && flyoutFor.level === item.value;
                                return (
                                    <li key={item.value}>
                                        <button
                                            style={{
                                                ...pillItemStyle,
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                ...((hoveredItem === `main-${item.value}` || openHere) ? { backgroundColor: '#ddd6fe', color: '#5b21b6' } : {}),
                                                ...activeStyle(item.value)
                                            }}
                                            onClick={() => pickLevel(item.value)}
                                            onMouseEnter={() => { setHoveredItem(`main-${item.value}`); setFlyoutFor(isDiv ? { kind: 'Division', level: item.value } : null); }}
                                            onMouseLeave={() => setHoveredItem(null)}
                                        >
                                            <span>{item.label}</span>{isDiv ? <span aria-hidden>▸</span> : null}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="kp-scroll-fade" aria-hidden>⌄</div>
                    </div>

                    {flyoutFor && (
                        <div style={{
                            backgroundColor: '#ffffff', border: '1px solid #ddd6fe', borderRadius: '0.65rem',
                            padding: '0.5rem', minWidth: '170px', maxHeight: '50vh', overflowY: 'auto',
                            boxShadow: '0 18px 44px rgba(76,29,149,0.28)'
                        }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.5px', color: '#7c3aed', textTransform: 'uppercase', padding: '0.1rem 0.5rem 0.45rem' }}>
                                {flyoutFor.kind} · {flyoutFor.level}
                            </div>
                            {(flyoutFor.kind === 'Division' ? DIVISION_VALUES : UNIT_VALUES).map(opt => {
                                const key = `${flyoutFor.kind}-${flyoutFor.level}-${opt}`;
                                const currentVal = flyoutFor.kind === 'Division' ? currentDivision : currentUnit;
                                const active = currentLevel === flyoutFor.level && currentVal === opt;
                                return (
                                    <button
                                        key={opt}
                                        style={{
                                            ...pillItemStyle,
                                            ...(hoveredItem === key ? { backgroundColor: '#ddd6fe', color: '#5b21b6' } : {}),
                                            ...(active ? { backgroundColor: '#8b5cf6', color: '#ffffff' } : {})
                                        }}
                                        onMouseEnter={() => setHoveredItem(key)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                        onClick={() => flyoutFor.kind === 'Division'
                                            ? pickDivision(flyoutFor.level, opt)
                                            : pickUnit(flyoutFor.level, opt)}
                                    >
                                        {active ? '✓ ' : ''}{opt}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
